import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "@/types/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { addFineTuningDetails, getFineTuningById } from "@/containers/FineTuning/reducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { Card, CardContent } from "@/aible/Card/Card";
import { Badge } from "@/aible/Badge/Badge";
import { Progress } from "@/aible/Progress/Progress";
import { Separator } from "@/aible/Separator/Separator";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import Tooltip from "components/Inputs/Tooltip";
import { ScrollArea } from "@/aible/ScrollArea/ScrollArea";
const ModelEvaluationDetailsModal = ({ open, onOpenChange, selectedEvaluatorId }) => {
    const dispatch = useDispatch();
    const [isLoading, updateIsLoading] = useState(false);
    useEffect(() => {
        if (selectedEvaluatorId) {
            updateIsLoading(true);
            dispatch(addFineTuningDetails(selectedEvaluatorId)).finally(() => updateIsLoading(false));
        }
    }, [selectedEvaluatorId]);
    const evaluator = useSelector((state) => getFineTuningById(state, selectedEvaluatorId));
    if (!evaluator?.details) {
        return null;
    }
    const tasks = Object.keys(evaluator?.details?.tasks || {}).map((task) => {
        const metrics = Object.keys(evaluator.details.tasks[task].metrics || {});
        return {
            task,
            metrics: metrics.map((metric) => {
                const scores = Object.keys(evaluator.details.tasks[task].metrics[metric].scores || {});
                return {
                    metric,
                    scores: scores.map((score) => ({
                        score,
                        value: Math.round(evaluator.details.tasks[task].metrics[metric].scores[score].value * 100)
                    }))
                };
            })
        };
    });
    const groups = Object.keys(evaluator.details.groups || {}).map((group) => {
        const metrics = Object.keys(evaluator.details.groups[group].metrics || {});
        return {
            group,
            metrics: metrics.map((metric) => {
                const scores = Object.keys(evaluator.details.groups[group].metrics[metric].scores || {});
                return {
                    metric,
                    scores: scores.map((score) => ({
                        score,
                        value: Math.round(evaluator.details.groups[group].metrics[metric].scores[score].value * 100)
                    }))
                };
            })
        };
    });
    const getProgressVariant = (value) => {
        if (value > 60) {
            return "success";
        }
        if (value > 40) {
            return "warning";
        }
        return "destructive";
    };
    const getBenchmarkTooltip = (benchmarkType) => {
        if (benchmarkType === "gsm8k") {
            return "State-of-the-art language models can match human performance on many tasks, but they still struggle to robustly perform multi-step mathematical reasoning. To diagnose the failures of current models and support research, we introduce GSM8K, a dataset of 8.5K high quality linguistically diverse grade school math word problems. We find that even the largest transformer models fail to achieve high test performance, despite the conceptual simplicity of this problem distribution.";
        }
    };
    const getBenchmarkTicks = (benchmarkType) => {
        if (benchmarkType === "gsm8k") {
            return [40, 60];
        }
    };
    return (React.createElement(Dialog, { open: open, onOpenChange: onOpenChange },
        React.createElement(DialogContent, { size: "xl" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Model Evaluation Report")),
            isLoading && React.createElement(LoadingIndicator, { className: "m-0" }),
            !isLoading && (React.createElement(React.Fragment, null,
                React.createElement("div", null,
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg" },
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement(FontAwesomeIcon, { icon: faHashtag }),
                            React.createElement("div", null,
                                React.createElement("div", { className: "text-xs text-(--muted-foreground)" }, "Evaluation ID"),
                                React.createElement("div", { className: "font-mono" }, evaluator?.details?.id))),
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement(FontAwesomeIcon, { icon: faHashtag }),
                            !!evaluator?.details?.created_at && (React.createElement("div", null,
                                React.createElement("div", { className: "text-xs text-(--muted-foreground)" }, "Created"),
                                React.createElement("div", null, format(new Date(evaluator?.details?.created_at + "Z"), "MM/dd/yyyy, hh:mm:ss aa"))))),
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement(FontAwesomeIcon, { icon: faHashtag }),
                            React.createElement("div", null,
                                React.createElement("div", { className: "text-xs text-(--muted-foreground)" }, "Job"),
                                React.createElement("div", { className: "font-mono" }, evaluator?.details?.job))))),
                React.createElement("div", { className: "space-y-8" },
                    groups.map((group) => (React.createElement("div", { key: group.group },
                        React.createElement("div", { className: "text-lg font-semibold text-foreground" }, "Top Evaluation Metrics"),
                        React.createElement("div", { className: "flex mb-4 items-center" },
                            React.createElement("div", null, "This model was evaluated using the following benchmark: " + group.group),
                            getBenchmarkTooltip(group.group) !== undefined && (React.createElement("div", null,
                                React.createElement(Tooltip, { trigger: ["hover"], overlay: React.createElement("div", null, getBenchmarkTooltip(group.group)) })))),
                        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, group.metrics.map((metric) => (React.createElement(Card, { key: metric.metric },
                            React.createElement(CardContent, null,
                                React.createElement("div", { className: "space-y-2" },
                                    React.createElement("div", { className: "flex items-center justify-between" },
                                        React.createElement(Badge, { variant: "outline" }, "percentage")),
                                    metric.scores.map((score) => (React.createElement("div", { className: "space-y-1", key: score.score },
                                        React.createElement("div", { className: "font-medium text-lg" }, score.score),
                                        React.createElement("div", { className: "font-medium text-2xl" },
                                            score.value,
                                            "%"),
                                        React.createElement(Progress, { value: score.value, ticks: getBenchmarkTicks(group.group), indicatorColor: getProgressVariant(score.value) }))))))))))))),
                    React.createElement(Separator, null),
                    React.createElement(ScrollArea, { className: "h-[300px]" },
                        React.createElement("div", { className: "space-y-4" }, tasks.map((task) => (React.createElement("div", { key: task.task, className: "space-y-2" },
                            React.createElement("div", { className: "text-lg font-semibold mb-4 text-foreground" },
                                task.task,
                                ": Task Performance"),
                            React.createElement(Card, null,
                                React.createElement(CardContent, null,
                                    React.createElement("div", { className: "grid grid-cols-2 gap-8" }, task.metrics.map((metric) => (React.createElement("div", null,
                                        React.createElement("div", { className: "text-sm font-medium mb-3 text-(--muted-foreground)" }, metric.metric),
                                        React.createElement("div", { className: "space-y-2" }, metric.scores.map((score) => (React.createElement("div", { className: "flex justify-between items-center py-1" },
                                            React.createElement("div", { className: "text-sm text-(--foreground)" }, score.score),
                                            React.createElement(Badge, { variant: "secondary" },
                                                score.value,
                                                "%"))))))))))))))))),
                React.createElement(Separator, null),
                React.createElement("div", { className: "flex justify-between items-center text-sm text-(--muted-foreground)" },
                    React.createElement("div", null,
                        "Namespace: ",
                        evaluator?.details?.namespace),
                    !!evaluator?.details?.updated_at && (React.createElement("div", null,
                        "Last updated: ",
                        format(new Date(evaluator?.details?.updated_at + "Z"), "MM/dd/yyyy, hh:mm:ss aa")))))))));
};
export default ModelEvaluationDetailsModal;
